import { subscriptionPlanLabel, formatCreditCount } from "@shared/subscription-presentation";
import { useUsageBalance, useUsageActions } from "@/hooks/use-usage";

function formatMonthDay(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

export function SettingsUsageTab({ onUpgrade, onViewAll }: { onUpgrade: () => void; onViewAll: () => void }) {
  const bal = useUsageBalance();
  const actions = useUsageActions(10);

  if (bal.isLoading) {
    return <div className="p-8 text-sm text-neutral-500">Loading usage…</div>;
  }
  if (bal.error || !bal.data) {
    return (
      <div className="p-8 text-sm text-red-500" data-testid="usage-tab-error">
        Failed to load usage.
      </div>
    );
  }

  const { balance, plan, usedThisCycle } = bal.data;
  const total = plan?.creditsPerMonth ?? 0;
  const pctRemaining = total > 0 ? Math.max(0, Math.min(100, (balance / total) * 100)) : 0;

  return (
    <div className="space-y-8 p-1" data-testid="settings-usage-tab">
      <header className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">{plan ? subscriptionPlanLabel(plan) : "No plan"}</h2>
          {!plan?.isPromo && plan?.renewsAt && (
            <p className="text-sm text-neutral-500">
              {plan.status === "trialing" ? "Trial ends" : "Renews"} {formatMonthDay(plan.renewsAt)}
            </p>
          )}
          {!plan && (
            <p className="text-sm text-neutral-500">You don't have an active plan.</p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onUpgrade}
            className="rounded bg-black px-3 py-1.5 text-sm text-white hover:bg-neutral-800"
            data-testid="usage-tab-upgrade"
          >
            {plan ? "Upgrade" : "Choose plan"}
          </button>
        </div>
      </header>

      <section>
        <div className="mb-1 flex justify-between text-sm">
          <span>⚡ Credits</span>
          <span className="font-mono tabular-nums">
            {formatCreditCount(balance)} / {formatCreditCount(total)}
          </span>
        </div>
        <div className="h-2 rounded bg-neutral-100">
          <div
            className={`h-2 rounded transition-all ${
              pctRemaining < 5
                ? "bg-red-500"
                : pctRemaining < 10
                ? "bg-amber-500"
                : pctRemaining < 20
                ? "bg-yellow-500"
                : "bg-black"
            }`}
            style={{ width: `${pctRemaining}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-neutral-500">
          {formatCreditCount(usedThisCycle)} used {plan?.isPromo ? 'from your promotional credits' : 'this billing period'}
        </p>

        {plan && pctRemaining < 20 && pctRemaining >= 10 && (
          <div className="mt-3 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Heads up — you're under 20% for this cycle.
          </div>
        )}
        {plan && pctRemaining < 10 && pctRemaining >= 5 && (
          <div className="mt-3 rounded bg-amber-100 px-3 py-2 text-sm text-amber-900">
            Only {formatCreditCount(balance)} credits left.{" "}
            <button className="underline" onClick={onUpgrade}>Upgrade →</button>
          </div>
        )}
        {plan && pctRemaining < 5 && (
          <div className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-800">
            Critical — {formatCreditCount(balance)} credits left.{" "}
            <button className="underline" onClick={onUpgrade}>Upgrade now →</button>
          </div>
        )}

        {plan && balance === 0 && (
          <div
            className="mt-3 rounded border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600"
            data-testid="usage-tab-bg-paused"
          >
            ⏸ Background sync paused (heartbeat refresh, brand DNA, daily perf ingest). Resumes automatically on renewal.
          </div>
        )}
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-medium">Usage record</h3>
          <button
            onClick={onViewAll}
            className="text-sm text-blue-600 hover:underline"
            data-testid="usage-tab-view-all"
          >
            View all →
          </button>
        </div>
        <div className="divide-y divide-neutral-100 rounded border border-neutral-200">
          {(actions.data?.rows ?? []).map((a: any) => (
            <div key={a.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <div>
                <div className="font-medium text-neutral-800">{a.actionLabel}</div>
                <div className="text-xs text-neutral-500">
                  {a.brandName ? `${a.brandName} · ` : ""}
                  {formatMonthDay(a.startedAt)}
                  {a.status !== "completed" ? ` · ${a.status}` : ""}
                </div>
                {Array.isArray(a.breakdown) && a.breakdown.length > 0 && (
                  <div className="mt-0.5 text-[11px] text-neutral-400">
                    {a.breakdown
                      .map((b: any) => `${b.label} ${Number(b.credits).toFixed(0)}`)
                      .join(" · ")}
                  </div>
                )}
              </div>
              <div className="font-mono tabular-nums text-neutral-700">
                −{Number(a.creditsActual).toFixed(0)}
              </div>
            </div>
          ))}
          {(actions.data?.rows ?? []).length === 0 && (
            <div className="p-6 text-center text-sm text-neutral-500">
              No usage this cycle yet.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
